import React, { useState } from "react";
import { useGetInvoicePdf } from "@/api/billingApi";
import { FaTimes, FaSpinner, FaDownload } from "react-icons/fa";

interface InvoicePdfDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber?: string;
}

const InvoicePdfDialog: React.FC<InvoicePdfDialogProps> = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
}) => {
  const {
    data: pdfResponse,
    isLoading,
    error,
  } = useGetInvoicePdf(invoiceId, {
    enabled: isOpen && !!invoiceId,
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  React.useEffect(() => {
    if (pdfResponse?.data) {
      // pdfResponse.data is already a blob when responseType: 'blob' is used
      const blob = pdfResponse.data;
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      // Cleanup function to revoke the URL when component unmounts
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [pdfResponse]);

  // Cleanup PDF URL when dialog closes
  React.useEffect(() => {
    if (!isOpen && pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [isOpen, pdfUrl]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `invoice-${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog - Full Screen */}
      <div className="relative bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Invoice PDF Viewer
            </h3>
            {invoiceNumber && (
              <p className="text-sm text-gray-500 mt-1">
                Invoice #{invoiceNumber}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              disabled={!pdfUrl}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-[#78C7D1] hover:text-[#4fa3b3] hover:bg-[#78C7D1] hover:bg-opacity-10 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download PDF"
            >
              <FaDownload className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-150"
              title="Close"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <FaSpinner className="w-8 h-8 text-[#78C7D1] animate-spin" />
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Failed to load PDF
                </h3>
                <p className="text-gray-500">
                  There was an error loading the invoice PDF. Please try again.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-[#78C7D1] text-white rounded-lg hover:bg-[#4fa3b3] transition-colors duration-150"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {pdfUrl && !isLoading && !error && (
            <div className="h-full">
              <object
                data={pdfUrl}
                type="application/pdf"
                className="w-full h-full border-0"
                title={`Invoice PDF - ${invoiceNumber || invoiceId}`}
              >
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-gray-500 text-6xl mb-4">📄</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      PDF Viewer Not Supported
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Your browser doesn't support inline PDF viewing.
                    </p>
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 bg-[#78C7D1] text-white rounded-lg hover:bg-[#4fa3b3] transition-colors duration-150"
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              </object>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePdfDialog;
